import type { PlatformSession } from '@t4s/api-client/model'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BUILD } from '../lib/build.ts'
import { readPlatformIdentity } from '../lib/gateway.ts'
import { SESSION_QUERY_KEY } from '../shell/app-shell.tsx'
import { useDocumentTitle } from '../shell/document-title.ts'
import { useServiceHealth } from '../shell/environment.ts'
import { EnvironmentPill } from '../shell/environment-badge.tsx'
import { Notice } from '../shell/notice.tsx'
import { Card } from '../tenants/card.tsx'
import { CopyButton } from '../tenants/copy-button.tsx'
import { formatInstantWithoutZone, ownerTimeZone } from '../tenants/instants.ts'
import { ThemeChoice } from '../theme/theme-controls.tsx'
import { PasswordCard } from './password-card.tsx'

/**
 * Your account — §4.4's last row, as far as the API allows today.
 *
 * It shows who is signed in, from the same read the shell already made: the nickname and
 * the full name, as the database has them **now** rather than as a token claimed them.
 *
 * **Change my password** is the Password card (`password-card.tsx`, TAS-15): current, new and
 * confirm, with the consequence — it ends every other session of the account — said before the
 * submit. **Last sign-in** is a row of the You card, from the same identity read: the account's
 * latest sign-in, in the owner's zone, and a plain sentence when it is later than the moment this
 * session began (somebody signed in elsewhere since).
 *
 * **P07 (2026-10-14) made it three cards** (You, Password, This deployment) and gave the deployment
 * card the API's own build beside the console's. The console's commit is shown in full with a Copy
 * control, its build time in the **owner's** zone with the zone named (§7.5, a platform record), and
 * the API's `build` comes from `GET /health` — the read the masthead already makes for the
 * environment word, observed through the same query, so this page asks for nothing new.
 *
 * **P09 (2026-10-15) added the Theme card** — System, Light or Dark, kept in this browser
 * (`theme/`). The masthead has a second, three-icon control for the same preference.
 *
 * **"This deployment"** is this task's own addition (`chore-console-build-identifier`): the
 * commit and build time `lib/build.ts` read from what Vite baked into this bundle, so a QA
 * report about this screen can also cite which commit it tested — the same reason
 * `apps/api`'s `GET /health` carries a `build` object. §5.1 rules out a footer for this, so
 * it sits here instead, beside the other read-only facts about this browser's session.
 */
/** A build's commit in full, monospace and copyable — or the word for why there is none. */
function CommitValue({ commit, noun }: { readonly commit: string | null; readonly noun: string }) {
  if (commit === null) {
    return <span className="muted">—</span>
  }
  return (
    <span className="copy-row">
      <code className="wrap">{commit}</code>
      <CopyButton value={commit} noun={noun} quiet />
    </span>
  )
}

/** An instant in the owner's zone, with the zone named beside it, or a dash. */
function ZonedInstant({ iso, zone }: { readonly iso: string | null; readonly zone: string }) {
  const formatted = formatInstantWithoutZone(iso, zone)
  if (formatted === '—') {
    return <span className="muted">—</span>
  }
  return (
    <>
      {formatted} <code className="muted">{zone}</code>
    </>
  )
}

/**
 * The account's latest sign-in: a dash while the identity is still being read, the words
 * "None recorded" for an account that has a session but no recorded sign-in (`null`), and the
 * instant in the owner's zone otherwise.
 */
function LastSignIn({
  lastSignInAt,
  zone,
}: {
  readonly lastSignInAt: string | null | undefined
  readonly zone: string
}) {
  if (lastSignInAt === undefined) {
    return <span className="muted">—</span>
  }
  if (lastSignInAt === null) {
    return <span className="muted">None recorded</span>
  }
  return <ZonedInstant iso={lastSignInAt} zone={zone} />
}

/**
 * Whether the account has signed in somewhere else since this session began: `lastSignInAt` is the
 * account's latest sign-in and `signedInAt` is this session's start, so a later `lastSignInAt` is a
 * sign-in that was not this one. Equal, earlier, missing or unparseable instants say nothing.
 */
function signedInSince(identity: PlatformSession | undefined): boolean {
  if (identity === undefined || identity.lastSignInAt === null) {
    return false
  }
  const last = Date.parse(identity.lastSignInAt)
  const began = Date.parse(identity.signedInAt)
  return !Number.isNaN(last) && !Number.isNaN(began) && last > began
}

/**
 * The `lastSignInAt` the owner has dealt with by changing the password in this session, kept in the
 * query client so that it survives leaving Your account and coming back, and is thrown away with
 * everything else when the session ends (`lib/runtime.ts` clears the cache then).
 *
 * The "signed in elsewhere" warning exists to prompt exactly this change, which ends every other
 * session — so once it has succeeded the warning is answered and goes. It is keyed on the sign-in it
 * was shown for rather than hidden outright: a sign-in that happens *after* the change is a new one,
 * `lastSignInAt` moves, and the warning returns.
 */
const DEALT_WITH_SIGN_IN_KEY = ['platform', 'sign-in-dealt-with'] as const

export function AccountScreen() {
  useDocumentTitle('Your account')
  const identity = useQuery({ queryKey: SESSION_QUERY_KEY, queryFn: readPlatformIdentity })
  const health = useServiceHealth()
  const zone = ownerTimeZone()
  const api = health.data?.build
  const queryClient = useQueryClient()
  const dealtWith = useQuery<string | null>({
    queryKey: DEALT_WITH_SIGN_IN_KEY,
    // Never fetched: it is only ever written, by a successful change below.
    queryFn: () => null,
    enabled: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
  })
  const signedInElsewhere =
    signedInSince(identity.data) && identity.data?.lastSignInAt !== dealtWith.data

  return (
    <main className="page page-narrow">
      <div className="page-header">
        <div>
          <h1>Your account</h1>
          <p className="page-lead">
            The administrator this browser is signed in as, as the database has them now.
          </p>
        </div>
      </div>

      <Card title="You">
        <div className="card-body">
          <dl className="kv">
            <dt>Nickname</dt>
            <dd className="mono">{identity.data?.nickname ?? '—'}</dd>
            <dt>Full name</dt>
            <dd>{identity.data?.fullName ?? '—'}</dd>
            <dt>Last sign-in</dt>
            <dd>
              <LastSignIn lastSignInAt={identity.data?.lastSignInAt} zone={zone} />
            </dd>
          </dl>
          {signedInElsewhere ? (
            <Notice tone="warning">
              There has been a sign-in to this account elsewhere since this session began. If that
              was not you, change the password below: that ends every other session.
            </Notice>
          ) : null}
        </div>
      </Card>

      <Card title="Theme">
        <div className="card-body">
          <ThemeChoice />
        </div>
      </Card>

      <PasswordCard
        onChanged={() => {
          // Every other session has just ended, so the sign-in the warning was about is answered.
          queryClient.setQueryData(DEALT_WITH_SIGN_IN_KEY, identity.data?.lastSignInAt ?? null)
        }}
      />

      <Card
        title="This deployment"
        description="What this console and the API it talks to were built from."
        action={<EnvironmentPill />}
      >
        <div className="card-body">
          <dl className="kv">
            <dt>Console commit</dt>
            <dd>
              <CommitValue commit={BUILD.commit} noun="console commit" />
            </dd>
            <dt>Console built</dt>
            <dd>
              <ZonedInstant iso={BUILD.time} zone={zone} />
            </dd>
            <dt>API commit</dt>
            <dd>
              <CommitValue commit={api?.commit ?? null} noun="API commit" />
            </dd>
            <dt>API release</dt>
            <dd>
              {api === undefined ? (
                <span className="muted">—</span>
              ) : (
                (api.version ?? <span className="muted">Not a release</span>)
              )}
            </dd>
            <dt>API deployed</dt>
            <dd>
              <ZonedInstant iso={api?.deployedAt ?? null} zone={zone} />
            </dd>
          </dl>
          {health.isError && api === undefined ? (
            <p className="small muted" role="status">
              The API’s build could not be read just now. It is asked again in the background.
            </p>
          ) : null}
        </div>
      </Card>
    </main>
  )
}
